/**
 * When to nudge a player to install the web app, and what the nudge may offer.
 *
 * Agreed policy (TRIB-2753, client-side only - no server record of installs):
 *   - iOS: shown once, ever. Safari cannot tell us whether the app is already on the
 *     home screen, so asking again would nag players who already said yes.
 *   - Android (native prompt available): shown on first eligibility, then again every
 *     two weeks. From the second showing on the nudge carries a "don't show again" box.
 *   - Never inside the installed app, never after the browser reported an install.
 *
 * The decisions are pure functions over a small state record so they can be reasoned
 * about (and unit-tested) without a browser. The state lives in localStorage; losing it
 * (cleared site data, another browser) simply restarts the schedule, which is the right
 * failure mode for a nudge.
 */
import LS from '../../utils/localStorageUtils';
import type { InstallMethod } from './installPrompt';

export const INSTALL_NUDGE_STORAGE_KEY = 'pwa_install_nudge';
export const ANDROID_REPEAT_INTERVAL_MS = 14 * 24 * 60 * 60 * 1000;

export type InstallNudgeState = {
	/** How many times the nudge has been shown on this browser. */
	shownCount: number;
	/** Epoch ms of the last showing, or null when never shown. */
	lastShownAt: number | null;
	/** The player ticked "don't show again". */
	neverShow: boolean;
	/** The browser reported `appinstalled`, or the player accepted the native prompt. */
	installed: boolean;
};

export const INITIAL_NUDGE_STATE: InstallNudgeState = {
	shownCount: 0,
	lastShownAt: null,
	neverShow: false,
	installed: false,
};

export function readNudgeState(): InstallNudgeState {
	try {
		const raw = LS.get(INSTALL_NUDGE_STORAGE_KEY);
		if (!raw) return INITIAL_NUDGE_STATE;
		const parsed = JSON.parse(raw) as Partial<InstallNudgeState>;
		return {
			shownCount: typeof parsed.shownCount === 'number' ? parsed.shownCount : 0,
			lastShownAt: typeof parsed.lastShownAt === 'number' ? parsed.lastShownAt : null,
			neverShow: parsed.neverShow === true,
			installed: parsed.installed === true,
		};
	} catch {
		return INITIAL_NUDGE_STATE;
	}
}

export function writeNudgeState(state: InstallNudgeState): void {
	try {
		LS.set(INSTALL_NUDGE_STORAGE_KEY, JSON.stringify(state));
	} catch (error) {
		console.warn('[pwa] could not persist install nudge state:', error);
	}
}

/** Whether the nudge should be shown now, given how the app can be installed here. */
export function shouldShowInstallNudge(
	method: InstallMethod,
	state: InstallNudgeState,
	now: number = Date.now(),
): boolean {
	if (method === 'none') return false;
	if (state.installed || state.neverShow) return false;

	if (method === 'manual-ios') {
		return state.shownCount === 0;
	}

	// Native (Android) prompt: first time at once, then on the two-week schedule.
	if (state.shownCount === 0) return true;
	if (state.lastShownAt === null) return true;
	return now - state.lastShownAt >= ANDROID_REPEAT_INTERVAL_MS;
}

/** The "don't show again" option appears from the second Android showing on. */
export function offersNeverShowAgain(method: InstallMethod, state: InstallNudgeState): boolean {
	return method === 'native' && state.shownCount >= 1;
}

export const markNudgeShown = (
	state: InstallNudgeState,
	now: number = Date.now(),
): InstallNudgeState => ({
	...state,
	shownCount: state.shownCount + 1,
	lastShownAt: now,
});

export const markNeverShowAgain = (state: InstallNudgeState): InstallNudgeState => ({
	...state,
	neverShow: true,
});

export const markInstalled = (state: InstallNudgeState): InstallNudgeState => ({
	...state,
	installed: true,
});
